/**
 * Weight v2 admin — pure helpers and vocabularies. No React, no network; every
 * rule a screen depends on lives here so it can be unit-tested
 * (__tests__/format.test.js) and can never drift between the two queues.
 */

export const TONE = {
  success: { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', dot: '#22c55e' },
  danger:  { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca', dot: '#ef4444' },
  warning: { bg: '#fff7ed', fg: '#c2410c', border: '#fed7aa', dot: '#f97316' },
  info:    { bg: '#dbeafe', fg: '#1d4ed8', border: '#bfdbfe', dot: '#3b82f6' },
  purple:  { bg: '#ede7f6', fg: '#5e35b1', border: '#d1c4e9', dot: '#7e57c2' },
  grey:    { bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb', dot: '#9ca3af' },
}

// ── formatting ───────────────────────────────────────────────────────────────

export const fmtKg = (v, dp = 2) => (v == null || isNaN(Number(v)) ? '—' : `${Number(v).toFixed(dp)} kg`)

export const fmtInr = (v) => (v == null || isNaN(Number(v))
  ? '—'
  : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v)))

export const fmtDate = (v) => (v
  ? new Date(v).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  : '—')

export const fmtDay = (v) => (v
  ? new Date(v).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })
  : '—')

export const dimsText = (d) => (d && Number(d.l) > 0 && Number(d.b) > 0 && Number(d.h) > 0
  ? `${d.l} × ${d.b} × ${d.h} cm`
  : '—')

const HOUR = 3600000
const DAY = 24 * HOUR

function humanize(ms) {
  const abs = Math.abs(ms)
  if (abs < HOUR) return 'under an hour'
  if (abs < DAY) return `${Math.floor(abs / HOUR)}h`
  return `${Math.floor(abs / DAY)}d`
}

/**
 * The auto-accept clock on a NEW row. Overdue rows are the ones the 30-second
 * sweep will auto-accept on its next pass; "due in" under 48h is the panel's
 * own expiring band.
 */
export function deadlineLabel(deadlineAt, now = Date.now()) {
  if (!deadlineAt) return null
  const diff = new Date(deadlineAt).getTime() - now
  if (isNaN(diff)) return null
  if (diff < 0) return { text: `Overdue by ${humanize(diff)}`, tone: 'danger' }
  if (diff < 48 * HOUR) return { text: `Due in ${humanize(diff)}`, tone: 'warning' }
  return { text: `Due in ${humanize(diff)}`, tone: 'grey' }
}

/** "Raised 4d ago" — calendar age, for sorting the eye; the backend's working-day clock is the alert's. */
export function ageLabel(since, now = Date.now()) {
  if (!since) return '—'
  const diff = now - new Date(since).getTime()
  if (isNaN(diff) || diff < 0) return '—'
  return diff < HOUR ? 'just now' : `${humanize(diff)} ago`
}

// ── disputes ─────────────────────────────────────────────────────────────────

export const WD_STATUS = {
  NEW:                     { label: 'Awaiting merchant',        tone: 'info' },
  DISPUTED_BY_MERCHANT:    { label: 'Disputed by merchant',     tone: 'warning' },
  DISPUTED_BY_COURIER:     { label: 'Raised with courier',      tone: 'purple' },
  ACCEPTED_BY_MERCHANT:    { label: 'Accepted by merchant',     tone: 'success' },
  AUTO_ACCEPTED_BY_SYSTEM: { label: 'Auto-accepted',            tone: 'success' },
  ACCEPTED_BY_COURIER:     { label: 'Courier accepted',         tone: 'success' },
  ACCEPTED_PROSHIP:        { label: "On Prozo's account",       tone: 'success' },
  REJECTED_PROSHIP:        { label: 'Rejected — merchant pays', tone: 'danger' },
  SETTLED_PROSHIP:         { label: 'Settled at revised weight', tone: 'warning' },
}

export const WD_STATUS_OPTIONS = Object.keys(WD_STATUS).map((k) => ({ value: k, label: WD_STATUS[k].label }))

/**
 * Dispute-queue rows as a CSV the team can work through or send to a courier: one line per discrepancy,
 * plain numbers (not "₹1,234.00") so a spreadsheet can add them up, evidence as links, every remark.
 */
export function toQueueCsv(rows, merchantNameById = {}) {
  const num = (v) => (v == null || isNaN(Number(v)) ? '' : Number(v))
  const when = (v) => (v ? fmtDate(v) : '')
  // 27 Oct (business): wallet type, courier account code, and both weights rounded up to the merchant's rate-card
  // slab with their difference. 28 Oct: items as the MIS writes them (Items Quantity / Total Quantity), excess weight
  // slab to slab, the price at each weight, the passbook's word for the hold, and when the merchant acted.
  const head = ['AWB', 'Order ref', 'Merchant', 'Merchant ID', 'Wallet type', 'Courier', 'Courier account code', 'Items Quantity', 'Total Quantity',
    'Status', 'Declared weight (kg)', 'Courier billed weight (kg)', 'Brand pushed slab weight (kg)', 'Courier billed slab weight (kg)',
    'Slab difference (kg)', 'Excess weight (kg)', 'Price at declared weight (Rs)', 'Price at billed weight (Rs)', 'Excess amount (Rs)',
    'Hold', 'Hold amount (Rs)', 'Raised on', 'Merchant action date', 'Deadline', 'Merchant images',
    'Packing video', 'Courier images', 'Remarks']
  const lines = (rows || []).map((r) => [
    r.awb, r.orderId, r.merchantName || merchantNameById[r.merchantId] || '', r.merchantId, r.walletType || '', r.courier,
    r.courierAccountCode || '', r.items || '', num(r.itemQty),
    (WD_STATUS[r.status] && WD_STATUS[r.status].label) || r.status,
    num(r.originalWeight), num(r.appliedWeight), num(r.brandSlabKg), num(r.courierSlabKg), num(r.slabDiffKg),
    num(excessWeightKg(r)), num(r.priceAtDeclaredRs), num(r.priceAtBilledRs), num(r.excessAmount),
    holdText(r), num(r.holdAmount),
    when(r.createdAt), when(r.merchantActionAt), when(r.deadlineAt),
    (r.merchantImageLinks || []).join(' | '), r.videoLink || '', (r.courierImageLinks || []).join(' | '),
    (r.statusLog || []).filter((e) => e.remark).map((e) => `${when(e.at)} ${e.by || ''}: ${e.remark}`).join(' | '),
  ])
  const cell = (v) => {
    let s = v == null ? '' : String(v)
    // Merchant-typed text (remarks, order refs) must not run as a spreadsheet formula when the file is opened.
    if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [head].concat(lines).map((l) => l.map(cell).join(',')).join('\r\n')
}

/** Excess weight slab to slab (28 Oct): courier-billed slab − brand-pushed slab; the raw excess where no slab was recorded. */
export function excessWeightKg(row) {
  if (!row) return null
  return row.slabDiffKg != null ? row.slabDiffKg : row.excessKg
}

// The merchant ended up paying on these: accepted, auto-accepted, rejected, or settled at a revised weight.
const MERCHANT_PAID = new Set(['ACCEPTED_BY_MERCHANT', 'AUTO_ACCEPTED_BY_SYSTEM', 'REJECTED_PROSHIP', 'SETTLED_PROSHIP'])

/**
 * The hold as the passbook tells it (28 Oct): placed / debited / released. A hold released before the merchant
 * paid (e.g. the 24 Oct release of non-pilot holds) was followed by a direct debit, so it says both.
 */
export function holdText(row) {
  if (!row || !row.holdStatus) return ''
  switch (row.holdStatus) {
    case 'HELD': return 'Hold placed'
    case 'CONVERTED': return 'Hold debited'
    case 'FAILED': return 'Hold failed'
    case 'RELEASED': return row.isClosed && MERCHANT_PAID.has(row.status) ? 'Hold released · charged directly' : 'Hold released'
    default: return row.holdStatus
  }
}

export const HOLD_STATUS = {
  HELD:      { label: 'On hold',       tone: 'warning' },
  RELEASED:  { label: 'Hold released', tone: 'grey' },
  CONVERTED: { label: 'Hold debited',  tone: 'info' },
  FAILED:    { label: 'Hold failed',   tone: 'danger' },
}

// The admin's tabs. 'dispute' is the Prozo queue, so it is the default.
export const WD_TABS = [
  { key: 'dispute',  label: 'Needs a decision' },
  { key: 'action',   label: 'With merchants' },
  { key: 'resolved', label: 'Resolved' },
  // 27 Oct (business): the merchant never acted before the window ran out — kept out of Resolved.
  { key: 'auto',     label: 'Auto Accepted' },
  { key: 'all',      label: 'All' },
]

/** The bulk file's ACTION values and what each does to the merchant's money (business, 27 Oct). */
export const BULK_ACTIONS = [
  { value: 'ACCEPTED_PROSHIP', help: "Accept on Prozo's account: the merchant is not charged and any wallet hold is released." },
  { value: 'ACCEPTED_BY_COURIER', help: 'Courier accepted the dispute: the merchant is not charged and any wallet hold is released.' },
  { value: 'ACCEPTED_BY_MERCHANT', help: 'Close as accepted by the merchant: the merchant pays the additional freight; a wallet hold becomes the debit.' },
  { value: 'REJECTED_PROSHIP', help: "Reject the merchant's dispute: the merchant pays the additional freight; a wallet hold becomes the debit." },
  { value: 'SETTLED_PROSHIP', help: 'Settle at the weight in Revised_Weight_kg: the merchant pays what their own rate card charges at that weight and the rest of the hold is released.' },
]

/** The queue's shipment filter (28 Oct): B2C by default; "ALL" sends no filter at all. */
export const SHIPMENT_TYPE_OPTIONS = [
  { value: 'B2C', label: 'B2C' },
  { value: 'B2B', label: 'B2B' },
  { value: 'ALL', label: 'All' },
]
export const DEFAULT_SHIPMENT_TYPE = 'B2C'
/** The filter as the server takes it: B2C / B2B, or nothing for All. */
export const shipmentTypeParam = (v) => (v === 'B2C' || v === 'B2B' ? v : undefined)

export const BULK_DEFAULT_REMARK = 'Aligned and closed with Merchant'

/** A filled-in example of the bulk file: one row per kind of use, the remark left empty where the default applies. */
export function bulkSampleCsv() {
  return [
    'AWB_Number,ACTION,Remark,Revised_Weight_kg',
    'AWB0000000001,ACCEPTED_PROSHIP,,',
    'AWB0000000002,REJECTED_PROSHIP,Merchant agreed on call,',
    'AWB0000000003,SETTLED_PROSHIP,,1.5',
  ].join('\r\n')
}

/**
 * One bulk file's progress from its per-row results (WDBulkActionFile.wdActionStatusList: chunked lists of
 * WDActionWithStatus — actionStatus = executed, complete = processed, actionResult = the reason).
 */
export function bulkFileSummary(file) {
  const rows = ((file && file.wdActionStatusList) || []).flat().filter(Boolean)
  const failures = rows
    .filter((r) => r.complete && r.actionStatus !== true)
    .map((r) => ({ awb: r.awbNumber, reason: r.actionResult || 'Not executed' }))
  return {
    total: rows.length,
    ok: rows.filter((r) => r.actionStatus === true).length,
    failed: failures.length,
    failures,
    inProgress: !!file && file.fileProgress === 'IN_PROGRESS',
  }
}

/**
 * What Prozo can do to a dispute. Each entry says, in the words a reviewer
 * needs, what happens to the merchant's money — that is the whole difference
 * between the four and the reason the v1 dropdown of raw enum names was retired.
 * The values are the exact DisputeStatus names the decision endpoint accepts.
 */
export const WD_DECISIONS = [
  {
    value: 'ACCEPTED_PROSHIP',
    label: "Accept on Prozo's account",
    help: 'The merchant is not charged; a wallet hold is released. Prozo absorbs the additional freight.',
    tone: 'success',
  },
  {
    value: 'ACCEPTED_BY_COURIER',
    label: 'Courier accepted the dispute',
    help: 'The courier agreed to waive the charge. The merchant is not charged; a wallet hold is released. Weights team only — a remark is required.',
    tone: 'success',
    remarkRequired: true,
    restrictedTo: 'weights team',
  },
  {
    value: 'REJECTED_PROSHIP',
    label: "Reject the merchant's dispute",
    help: 'The merchant pays the additional freight now; a wallet hold becomes the debit.',
    tone: 'danger',
  },
  {
    value: 'SETTLED_PROSHIP',
    label: 'Settle at a revised weight',
    help: "Pick a weight between the merchant's declared and the courier's billed figure. The merchant pays what their own rate card charges at that weight and the rest of the hold is released — no amount is typed. A remark is required.",
    tone: 'warning',
    remarkRequired: true,
    needsWeight: true,
  },
  {
    value: 'DISPUTED_BY_COURIER',
    label: 'Raise with the courier',
    help: "Send the merchant's evidence to the courier. The dispute stays open under \"Raised with courier\".",
    tone: 'warning',
  },
  {
    value: 'DISPUTED_BY_MERCHANT',
    label: "File the dispute on the merchant's behalf",
    help: 'For a merchant who sent their evidence outside the panel. The row moves to "Disputed by merchant" exactly as if they had clicked Dispute. A remark is required.',
    tone: 'info',
    remarkRequired: true,
    onBehalf: true,
  },
]

const CLOSED = new Set(['ACCEPTED_BY_MERCHANT', 'AUTO_ACCEPTED_BY_SYSTEM', 'ACCEPTED_BY_COURIER', 'ACCEPTED_PROSHIP', 'REJECTED_PROSHIP', 'SETTLED_PROSHIP'])

export const isClosedStatus = (status) => CLOSED.has(status)

/**
 * Decisions available for one row. A closed row has none (the server refuses
 * anyway — "already closed"); a row already with the courier cannot be raised
 * with the courier again; filing on the merchant's behalf only makes sense while
 * the row is still awaiting the merchant (NEW).
 */
export function decisionsFor(row) {
  if (!row || row.isClosed || isClosedStatus(row.status)) return []
  let list = WD_DECISIONS
  if (row.status !== 'NEW') list = list.filter((d) => !d.onBehalf)
  if (row.status === 'DISPUTED_BY_COURIER') list = list.filter((d) => d.value !== 'DISPUTED_BY_COURIER')
  return list
}

/** The open interval a settlement weight must fall in: (declared, courier billed), kg. */
export function settlementBounds(row) {
  if (!row) return null
  const minKg = Number(row.originalWeight)
  const maxKg = Number(row.appliedWeight)
  if (!Number.isFinite(minKg) || !Number.isFinite(maxKg) || maxKg <= minKg) return null
  return { minKg, maxKg }
}

/**
 * Why the Confirm button is disabled for one decision, or '' when it may go.
 * Client-side echo of the server's rules (WdActionAccessRules / WeightSettlementRules)
 * so the reviewer reads the reason here instead of in an error toast.
 */
export function decisionBlocker(decision, { remark, weightKg, row } = {}) {
  if (!decision) return 'Choose a decision.'
  if (decision.remarkRequired && !String(remark || '').trim()) return 'A remark is required for this decision.'
  if (decision.needsWeight) {
    const bounds = settlementBounds(row)
    if (!bounds) return 'This row has no weight gap to settle inside.'
    const w = Number(weightKg)
    if (!(w > 0)) return 'Enter the settled weight in kg.'
    if (w <= bounds.minKg) return `Settle above the declared ${fmtKg(bounds.minKg)} — for no charge, use "Accept on Prozo's account".`
    if (w >= bounds.maxKg) return `Settle below the courier's ${fmtKg(bounds.maxKg)} — for the full charge, use "Reject the merchant's dispute".`
  }
  return ''
}

// ── freeze requests ──────────────────────────────────────────────────────────

export const WF_STATUS = {
  AWAITING_REVIEW:           { label: 'Awaiting review',  tone: 'warning' },
  ADDITIONAL_DATA_REQUESTED: { label: 'Data requested',   tone: 'info' },
  DATA_SUBMITTED:            { label: 'Data submitted',   tone: 'purple' },
  APPROVED:                  { label: 'Approved',         tone: 'success' },
  REJECTED:                  { label: 'Rejected',         tone: 'danger' },
  WITHDRAWN:                 { label: 'Withdrawn',        tone: 'grey' },
  SUPERSEDED:                { label: 'Superseded',       tone: 'grey' },
  PROPOSED:                  { label: 'Proposed to merchant', tone: 'purple' },
  PROPOSAL_DECLINED:         { label: 'Proposal declined', tone: 'grey' },
}

/** Who put the request on the table (MerchantWeightFreezeRequest.Source); missing = the merchant. */
export const WF_SOURCE_LABEL = {
  MERCHANT: 'Raised by the merchant',
  PROZO_PROPOSED: 'Proposed by Prozo (auto-proposal engine)',
  PROZO_SEEDED: 'Set by Prozo — carried over from the courier freeze',
}

export const WF_OPEN = ['AWAITING_REVIEW', 'ADDITIONAL_DATA_REQUESTED', 'DATA_SUBMITTED']
/** The server's "open" filter also lists proposals waiting on the merchant. */
export const WF_LISTED_OPEN = WF_OPEN.concat('PROPOSED')

export const WF_TABS = [
  { key: 'open',                      label: 'Open' },
  { key: 'AWAITING_REVIEW',           label: 'Awaiting review' },
  { key: 'DATA_SUBMITTED',            label: 'Data submitted' },
  { key: 'ADDITIONAL_DATA_REQUESTED', label: 'Waiting on merchant' },
  { key: 'PROPOSED',                  label: 'Proposed by Prozo' },
  { key: 'APPROVED',                  label: 'Approved' },
  { key: 'REJECTED',                  label: 'Rejected' },
  { key: 'all',                       label: 'All' },
]

/** Tab → count, from the /counts map (status → n). 'open' sums what the server's open filter lists. */
export function wfTabCounts(counts = {}) {
  const n = (k) => Number(counts[k] || 0)
  const out = {}
  WF_TABS.forEach((t) => {
    if (t.key === 'open') out[t.key] = WF_LISTED_OPEN.reduce((s, k) => s + n(k), 0)
    else if (t.key === 'all') out[t.key] = Object.values(counts).reduce((s, v) => s + Number(v || 0), 0)
    else out[t.key] = n(t.key)
  })
  return out
}

export const WF_LEVEL_LABEL = { SKU: 'SKU', SUB_CATEGORY: 'Sub-category', COMBO: 'Combination' }

export const WF_MAX_SUBMISSIONS = 3

export const WF_REJECT_REASONS = [
  { value: 'CONTRADICTORY_DATA',     label: 'Contradictory data available' },
  { value: 'COURIER_CONSTRAINT',     label: 'Not possible due to courier-side constraints' },
  { value: 'INADEQUATE_AFTER_LIMIT', label: `Additional data inadequate after ${WF_MAX_SUBMISSIONS} submissions` },
  { value: 'OTHER',                  label: 'Other (remark required)' },
]

/** The usual asks, offered as ticks so the merchant sees a consistent vocabulary. */
export const WF_ASK_ITEMS = [
  'Packing video, one unbroken clip ending on the scale',
  'Photo of the parcel on the weighing scale',
  'Length, breadth and height photos with a tape',
  'Photo of the shipping label',
  'Product page link',
]

export const WF_EVENT_TONE = {
  RAISED: 'grey', SUBMITTED: 'info', INFO_REQUESTED: 'info', NOTE: 'grey',
  APPROVED: 'success', DECLINED: 'danger', LIMIT_REACHED: 'warning', WITHDRAWN: 'grey', SUPERSEDED: 'grey',
  PROPOSED: 'purple', PROPOSAL_ACCEPTED: 'success', PROPOSAL_DECLINED: 'grey',
}

export const isWfOpen = (status) => WF_OPEN.includes(status)

/** Prozo may ask only while the merchant still has a submission left. */
export const canAskForData = (r) => !!r && isWfOpen(r.status) && r.status !== 'ADDITIONAL_DATA_REQUESTED'
  && Number(r.submissions || 0) < WF_MAX_SUBMISSIONS

/**
 * Round groups 1..N, N = max(last recorded round, current round), so an open
 * round with nothing in it still shows and a gap never collapses.
 */
export function groupRounds(history, currentRound) {
  const events = history || []
  const byRound = new Map()
  events.forEach((e) => {
    const n = e.round || 1
    if (!byRound.has(n)) byRound.set(n, [])
    byRound.get(n).push(e)
  })
  const highest = events.reduce((m, e) => Math.max(m, e.round || 1), 0)
  const top = Math.max(highest, currentRound || 0)
  const out = []
  for (let n = 1; n <= top; n += 1) out.push({ round: n, events: byRound.get(n) || [] })
  return out
}

/**
 * What the freeze reaches, in the unit of the level it was raised at.
 *
 * This used to read "N SKUs" for every level, counting the `covers` array. On a sub-category request
 * `covers` holds the sub-category itself, so approving a freeze over "Sandal" — twelve products in
 * that merchant's catalogue — was presented to the reviewer as "1 SKU". The decision on this screen
 * is how far the agreement extends, so the number and its unit have to match the level.
 */
export function coversText(r) {
  const n = (r && r.covers ? r.covers : []).length
  if (!n) return null
  if (r.level === 'SUB_CATEGORY') return `${n} sub-categor${n === 1 ? 'y' : 'ies'} — every SKU in ${n === 1 ? 'it' : 'them'}`
  if (r.level === 'COMBO') return `${n} combination${n === 1 ? '' : 's'}`
  return `${n} SKU${n === 1 ? '' : 's'}`
}

/**
 * The packing material the freeze is conditional on.
 *
 * Falls back to naming the id as unresolved rather than printing it bare. A raw ObjectId in this row
 * reads like a label, and the reviewer has no way to tell that the packaging behind the agreement
 * could not be found — the freeze only holds while that material is used, so an unresolvable one is
 * something to notice before approving, not after.
 */
export function packText(r) {
  if (!r) return null
  const weight = r.packSelfWeightG != null ? `${r.packSelfWeightG} g self weight` : null
  if (r.packLabel) return [r.packLabel, weight].filter(Boolean).join(' · ')
  if (r.packId) return [`Unresolved packing material (${r.packId})`, weight].filter(Boolean).join(' · ')
  return null
}
